import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { dateTime } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { useWhatsapp, useWhatsappMessages, type WhatsappDocumentType } from './api';
import { WHATSAPP_GREEN } from './WhatsappSend';

/**
 * What was sent on WhatsApp about a document, or to a contact (all their documents): newest
 * first, with who sent it, the number and the attached file. Shown only with WhatsApp on.
 */
export function WhatsappHistory(
  props: (
    | { documentType: WhatsappDocumentType; documentId: string; contactId?: never }
    | { contactId: string; documentType?: never; documentId?: never }
  ) & { sx?: SxProps<Theme> },
) {
  const wa = useWhatsapp();
  const { format } = useShop();
  const { sx, ...filter } = props;
  const messages = useWhatsappMessages(filter, wa.visible);
  const rows = messages.data?.data ?? [];
  if (!wa.visible || rows.length === 0) return null;
  return (
    <Card sx={sx}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <WhatsAppIcon sx={{ color: WHATSAPP_GREEN }} fontSize="small" />
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            WhatsApp messages
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          {rows.map((m) => (
            <Box
              key={m.id}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                pt: 1.5,
                '&:first-of-type': { borderTop: 0, pt: 0 },
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {m.recipientName ? `${m.recipientName} · ` : ''}+{m.phone}
                </Typography>
                {m.status === 'FAILED' ? (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    icon={<ErrorOutlineRounded />}
                    label="Not sent"
                  />
                ) : (
                  <Chip size="small" color="success" variant="outlined" label="Sent" />
                )}
                {props.contactId && m.documentRef && (
                  <Chip size="small" variant="outlined" label={m.documentRef} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {dateTime(m.createdAt, format)} · {m.sentBy.name}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {m.body}
              </Typography>
              {m.fileName && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                >
                  <AttachFileRounded sx={{ fontSize: 14 }} /> {m.fileName}
                </Typography>
              )}
              {m.error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {m.error}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
